import log from 'loglevel';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { LocationInfo, UserInfo } from '../../../../../../types';
import {
    TasksBackendClient,
    TaskDefinition,
    TaskType,
    GreetingTaskEventType,
} from '../../../../../../backends/tasks';

import closeIcon from 'assets/close-icon.svg';

import './style.scss';

export interface InternalGreetingProps {
    isShowing: boolean;
    onShow: () => void;
    onHide: () => void;
    openChat: () => void;
    tasksBackendClient?: TasksBackendClient;
    locationInfo: LocationInfo;
    userInfoExists: boolean;
    customerId?: string;
    userId?: string;
    forceHide?: boolean;
}

export type GreetingProps = TaskDefinition & InternalGreetingProps;

const Greeting: React.FC<GreetingProps> = ({
    id,
    message,
    hideDelay,
    style,
    tasksBackendClient,
    locationInfo,
    userInfoExists,
    customerId,
    userId,
    isShowing,
    onShow,
    onHide,
    openChat,
    forceHide,
}: GreetingProps) => {
    const hideTimer = React.useRef<ReturnType<typeof global.setTimeout> | undefined>(undefined);

    const doNotifyShown = React.useCallback(async () => {
        if (userInfoExists && customerId && userId) {
            tasksBackendClient?.postTaskEvent(
                TaskType.POPUP,
                id,
                GreetingTaskEventType.DISPLAY,
                locationInfo,
                { customerId, userId }
            );
        } else if (!userInfoExists) {
            tasksBackendClient?.postTaskEvent(
                TaskType.POPUP,
                id,
                GreetingTaskEventType.DISPLAY,
                locationInfo
            );
        }
    }, [id, tasksBackendClient, locationInfo, userInfoExists, customerId, userId]);

    const doHide = React.useCallback(() => {
        onHide();
    }, [onHide]);

    const clearHideTimer = React.useCallback(() => {
        if (hideTimer.current) {
            clearTimeout(hideTimer.current);
            hideTimer.current = undefined;
        }
    }, []);

    const doShow = React.useCallback(async () => {
        log.debug('showing greeting', id);
        onShow();
        doNotifyShown();
    }, [onShow]);

    // NOTE: storing current values in refs, as we need to access those from within a setTimeout(),
    //   which would otherwise always see the "old" values from when the setTimeout() was triggered:
    const userInfoExistsRef = React.useRef<boolean>(false);
    const locationInfoRef = React.useRef<LocationInfo | undefined>(undefined);
    const userInfoRef = React.useRef<UserInfo | undefined>(undefined);
    React.useEffect(() => {
        userInfoExistsRef.current = userInfoExists;
        locationInfoRef.current = locationInfo;
        userInfoRef.current = customerId && userId ? { customerId, userId } : undefined;
    }, [userInfoExists, customerId, userId, locationInfo]);

    const maybeShow = React.useCallback(async () => {
        if (!forceHide && message) {
            await doShow();

            if (hideDelay) {
                hideTimer.current = setTimeout(() => {
                    if (
                        userInfoExistsRef.current &&
                        userInfoRef.current?.customerId &&
                        userInfoRef.current?.userId
                    ) {
                        tasksBackendClient?.postTaskEvent(
                            TaskType.POPUP,
                            id,
                            GreetingTaskEventType.TIMEOUT,
                            locationInfoRef.current,
                            userInfoRef.current
                        );
                    } else if (!userInfoExistsRef.current) {
                        tasksBackendClient?.postTaskEvent(
                            TaskType.POPUP,
                            id,
                            GreetingTaskEventType.TIMEOUT,
                            locationInfoRef.current
                        );
                    }
                    doHide();
                }, hideDelay);
            }
        }

        return () => {
            clearHideTimer();
        };
    }, [id, message, hideDelay, userInfoExists, customerId, userId, forceHide]);

    React.useEffect(() => {
        // NOTE: wrap in timeout, to make it cancelable on component unmount:
        const timeout = setTimeout(() => {
            maybeShow();
        }, 0);

        return () => clearTimeout(timeout);
    }, []);

    const doManualHide = React.useCallback(() => {
        if (userInfoExists && customerId && userId) {
            tasksBackendClient?.postTaskEvent(
                TaskType.POPUP,
                id,
                GreetingTaskEventType.CLOSE,
                locationInfo,
                { customerId, userId }
            );
        } else if (!userInfoExists) {
            tasksBackendClient?.postTaskEvent(
                TaskType.POPUP,
                id,
                GreetingTaskEventType.CLOSE,
                locationInfo
            );
        }
        clearHideTimer();
        onHide();
    }, [id, onHide, tasksBackendClient, locationInfo, userInfoExists, customerId, userId]);

    React.useEffect(() => {
        if (!isShowing || forceHide) {
            clearHideTimer();
        }
    }, [isShowing, forceHide]);

    return isShowing && !forceHide ? (
        <div className={`rw-launcher-greeting rw-launcher-greeting-style-${style || 'default'}`}>
            <button
                className="rw-launcher-greeting-close"
                onClick={(e) => {
                    e.stopPropagation();
                    doManualHide();
                }}
            >
                <img src={closeIcon} />
            </button>
            <button
                className="rw-launcher-greeting-body"
                onClick={(e) => {
                    e.stopPropagation();
                    openChat && openChat();
                }}
            >
                <ReactMarkdown>{message}</ReactMarkdown>
            </button>
        </div>
    ) : null;
};

export default Greeting;
